import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { WorkData } from '../dto/EntityRepository';
import { useWorkListMarqueeStore } from '../store/workListMarqueeStore';
import {
  MARQUEE_GESTURE_MIN_PX,
  type MarqueeDragState,
  collectWorksInMarquee,
  marqueeDragToBox,
} from '../util/workListMarqueeUtils';

const AUTO_SCROLL_SPEED = 7;
const AUTO_SCROLL_EDGE_PX = 50;

export interface UseWorkListMarqueeOptions {
  workList: WorkData[];
  workListCompRef: RefObject<HTMLElement | null>;
  commitMarqueeHitWorksPreview: () => void;
  enabled: boolean;
}

export function useWorkListMarquee({
  workList,
  workListCompRef,
  commitMarqueeHitWorksPreview,
  enabled,
}: UseWorkListMarqueeOptions): {
  handleWorkListMouseDown: (e: ReactMouseEvent<HTMLElement>) => void;
  handleWorkListClickCapture: (e: ReactMouseEvent<HTMLElement>) => void;
  marqueeBox: ReturnType<typeof marqueeDragToBox>;
  marqueeSelecting: boolean;
} {
  const setHitWorks = useWorkListMarqueeStore((s) => s.setHitWorks);
  const clearHitWorks = useWorkListMarqueeStore((s) => s.clearHitWorks);

  const marqueeExceededThresholdRef = useRef(false);
  const suppressNextRowClickRef = useRef(false);
  const autoScrollRafRef = useRef<number | null>(null);
  const autoScrollDirRef = useRef<'up' | 'down' | null>(null);
  const lastPointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const [marqueeDrag, setMarqueeDrag] = useState<MarqueeDragState | null>(null);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current !== null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
    autoScrollDirRef.current = null;
  }, []);

  const applyPointerToMarquee = useCallback((clientX: number, clientY: number) => {
    const el = workListCompRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = clientX - r.left + el.scrollLeft;
    const cy = clientY - r.top + el.scrollTop;
    const origin = originRef.current;
    if (
      origin &&
      (Math.abs(cx - origin.x) >= MARQUEE_GESTURE_MIN_PX ||
        Math.abs(cy - origin.y) >= MARQUEE_GESTURE_MIN_PX)
    ) {
      marqueeExceededThresholdRef.current = true;
    }
    setMarqueeDrag((prev) => (prev ? { ...prev, currentX: cx, currentY: cy } : prev));
  }, [workListCompRef]);

  const startAutoScroll = useCallback((direction: 'up' | 'down') => {
    if (autoScrollDirRef.current === direction && autoScrollRafRef.current !== null) return;
    stopAutoScroll();
    autoScrollDirRef.current = direction;

    const scroll = () => {
      const el = workListCompRef.current;
      if (!el) {
        stopAutoScroll();
        return;
      }
      const maxScroll = el.scrollHeight - el.clientHeight;
      const nextTop = el.scrollTop + (direction === 'up' ? -AUTO_SCROLL_SPEED : AUTO_SCROLL_SPEED);
      el.scrollTop = Math.max(0, Math.min(maxScroll, nextTop));

      const ptr = lastPointerRef.current;
      if (ptr) applyPointerToMarquee(ptr.clientX, ptr.clientY);

      if (
        (direction === 'up' && el.scrollTop <= 0) ||
        (direction === 'down' && el.scrollTop >= maxScroll)
      ) {
        stopAutoScroll();
        return;
      }
      autoScrollRafRef.current = requestAnimationFrame(scroll);
    };

    autoScrollRafRef.current = requestAnimationFrame(scroll);
  }, [applyPointerToMarquee, stopAutoScroll, workListCompRef]);

  const syncAutoScrollFromPointer = useCallback((clientX: number, clientY: number) => {
    const el = workListCompRef.current;
    if (!el || !marqueeExceededThresholdRef.current) {
      stopAutoScroll();
      return;
    }
    const rect = el.getBoundingClientRect();
    const distanceFromTop = clientY - rect.top;
    const distanceFromBottom = rect.bottom - clientY;
    const canUp = el.scrollTop > 0;
    const canDown = el.scrollTop < el.scrollHeight - el.clientHeight;

    if (distanceFromTop < AUTO_SCROLL_EDGE_PX && canUp) {
      startAutoScroll('up');
    } else if (distanceFromBottom < AUTO_SCROLL_EDGE_PX && canDown) {
      startAutoScroll('down');
    } else {
      stopAutoScroll();
    }
  }, [startAutoScroll, stopAutoScroll, workListCompRef]);

  useLayoutEffect(() => {
    if (marqueeDrag === null) {
      if (enabled) {
        commitMarqueeHitWorksPreview();
      }
      return;
    }
    if (!enabled) {
      return;
    }
    const el = workListCompRef.current;
    const box = marqueeDragToBox(marqueeDrag);
    if (!el || !box) {
      clearHitWorks();
      return;
    }
    setHitWorks(collectWorksInMarquee(el, box, workList));
  }, [enabled, marqueeDrag, workList, setHitWorks, clearHitWorks, commitMarqueeHitWorksPreview]);

  /** enabled 꺼질 때 진행 중 제스처·프리뷰 정리 */
  useLayoutEffect(() => {
    if (enabled) return;
    clearHitWorks();
    setMarqueeDrag(null);
    stopAutoScroll();
  }, [enabled, clearHitWorks, stopAutoScroll]);

  const handleWorkListClickCapture = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    if (!suppressNextRowClickRef.current) return;
    suppressNextRowClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleWorkListMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLElement>) => {
      if (!enabled) return;
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-suppress-marquee]')) return;

      const el = workListCompRef.current;
      if (!el) return;

      e.preventDefault();
      marqueeExceededThresholdRef.current = false;
      lastPointerRef.current = { clientX: e.clientX, clientY: e.clientY };
      const rect = el.getBoundingClientRect();
      const ox = e.clientX - rect.left + el.scrollLeft;
      const oy = e.clientY - rect.top + el.scrollTop;
      originRef.current = { x: ox, y: oy };

      const onMove = (ev: globalThis.MouseEvent) => {
        lastPointerRef.current = { clientX: ev.clientX, clientY: ev.clientY };
        applyPointerToMarquee(ev.clientX, ev.clientY);
        syncAutoScrollFromPointer(ev.clientX, ev.clientY);
      };

      const onUp = () => {
        if (marqueeExceededThresholdRef.current) {
          suppressNextRowClickRef.current = true;
        }
        marqueeExceededThresholdRef.current = false;
        lastPointerRef.current = null;
        originRef.current = null;
        stopAutoScroll();
        setMarqueeDrag(null);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      setMarqueeDrag({
        originX: ox,
        originY: oy,
        currentX: ox,
        currentY: oy,
      });
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [applyPointerToMarquee, enabled, stopAutoScroll, syncAutoScrollFromPointer, workListCompRef]
  );

  const marqueeBox = marqueeDragToBox(marqueeDrag);

  return {
    handleWorkListMouseDown,
    handleWorkListClickCapture,
    marqueeBox,
    marqueeSelecting: !!marqueeDrag,
  };
}
